import { useCallback, useEffect, useRef } from 'react';
import { usePhoneNumber } from 'hooks';
import { looksLikePhoneNumber } from 'components/CallRailPhone/CallRailPhone';

const POLL_INTERVAL_MS = 500;
const POLL_MAX_MS = 15_000;

// Tracks the pending 'load' handler per iframe so we can replace it if
// sendToIframe is called again for the same element before the handler fires.
// Prevents listener accumulation when broadcastToIframes and observeLateIframes
// both target the same element in quick succession.
const _pendingLoad = new WeakMap<HTMLIFrameElement, () => void>();

function sendToIframe(
  iframe: HTMLIFrameElement,
  swappedPhone: string,
  widgetBaseUrl: string
): void {
  if (!iframe.src.startsWith(widgetBaseUrl)) return;
  const send = () =>
    iframe.contentWindow?.postMessage(
      { type: 'callrail:phone_swap', swappedPhone },
      widgetBaseUrl
    );

  // Replace any listener from a prior call so at most one is live per element.
  const prev = _pendingLoad.get(iframe);
  if (prev) iframe.removeEventListener('load', prev);

  const loadHandler = () => {
    _pendingLoad.delete(iframe);
    send();
  };
  _pendingLoad.set(iframe, loadHandler);

  send();
  // Re-send on load in case the iframe hadn't finished executing its JS when
  // we first tried (CallRail can resolve before the widget bundle is ready).
  // The lumis widget is a SPA that navigates client-side, so this fires at
  // most once per session; the receiver (use-widget-meta) is idempotent.
  iframe.addEventListener('load', loadHandler, { once: true });
}

function broadcastToIframes(swappedPhone: string, widgetBaseUrl: string): void {
  document
    .querySelectorAll<HTMLIFrameElement>('iframe')
    .forEach((iframe) => sendToIframe(iframe, swappedPhone, widgetBaseUrl));
}

function observeLateIframes(
  swappedPhone: string,
  widgetBaseUrl: string
): MutationObserver {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLIFrameElement) {
          sendToIframe(node, swappedPhone, widgetBaseUrl);
        }
        if (node instanceof HTMLElement) {
          node
            .querySelectorAll<HTMLIFrameElement>('iframe')
            .forEach((iframe) =>
              sendToIframe(iframe, swappedPhone, widgetBaseUrl)
            );
        }
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  return observer;
}

/**
 * Resolve the CallRail-swapped number for the current phone and postMessage it
 * to every widget iframe on the page.
 *
 * Widget iframes are cross-origin so CallRail's swap.js cannot reach their DOM.
 * This hook bridges the gap: once CallTrk is available it resolves the swap,
 * then broadcasts to any matching iframes immediately and to any that mount
 * later via MutationObserver.
 *
 * Returns a `resend` callback that re-broadcasts the already-resolved swap to
 * all current iframes. Use it when a widget SPA navigates to a new view after
 * the initial swap — e.g., call it from onReservationComplete so the
 * confirmation view receives the swapped number even though it mounted after
 * the initial broadcast.
 *
 * Safe to call unconditionally — exits early when WIDGET_BASE_URL is unset
 * or when CallTrk never loads within the poll window.
 */
export function useCallRailWidgetSync(): () => void {
  const phone = usePhoneNumber({ isLeadMode: false });
  const resolvedRef = useRef<{
    swappedPhone: string;
    widgetBaseUrl: string;
  } | null>(null);

  const resend = useCallback(() => {
    const r = resolvedRef.current;
    if (!r) return;
    // Fire immediately, then retry after 300 ms to cover the race where the SPA
    // confirmation route registers its message listener after client-side navigation
    // completes. SPA navigation never fires the iframe load event, so the
    // { once: true } load handler in sendToIframe cannot cover this gap.
    // The receiver is idempotent so a double-send is safe.
    broadcastToIframes(r.swappedPhone, r.widgetBaseUrl);
    setTimeout(() => broadcastToIframes(r.swappedPhone, r.widgetBaseUrl), 300);
  }, []);

  useEffect(() => {
    const WIDGET_BASE_URL = process.env.NEXT_PUBLIC_WIDGET_BASE_URL;
    if (!WIDGET_BASE_URL) return;

    resolvedRef.current = null;

    let observer: MutationObserver | undefined;
    let elapsed = 0;
    const poll = setInterval(() => {
      elapsed += POLL_INTERVAL_MS;
      if (elapsed > POLL_MAX_MS) {
        clearInterval(poll);
        return;
      }
      if (!(window as any).CallTrk) return;
      clearInterval(poll);

      (window as any).CallTrk.getSwapNumbers(
        [phone],
        (swapDictionary: Record<string, string>) => {
          // CallRail returns digit-only keys and values (e.g. { 8664953522: "8667801998" }).
          // Format the swapped number to NXX-NXX-XXXX so the widget displays it
          // consistently with other phone numbers sourced from microsite configs.
          const phoneDigits = phone.replace(/[^\d]/g, '');
          const resolved = swapDictionary[phoneDigits];
          if (resolved && looksLikePhoneNumber(resolved)) {
            const digits = resolved.replace(/\D/g, '');
            const formatted = `${digits.slice(0, 3)}-${digits.slice(
              3,
              6
            )}-${digits.slice(6)}`;
            resolvedRef.current = {
              swappedPhone: formatted,
              widgetBaseUrl: WIDGET_BASE_URL,
            };
            broadcastToIframes(formatted, WIDGET_BASE_URL);
            observer = observeLateIframes(formatted, WIDGET_BASE_URL);
          }
        }
      );
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(poll);
      observer?.disconnect();
    };
  }, [phone]);

  return resend;
}
